import React, { useEffect, useMemo, useState } from 'react';
import {
  MODAL_CONTENT_Z_INDEX,
  MODAL_OVERLAY_Z_INDEX,
  MODAL_PORTAL_Z_INDEX,
} from '@components/modals/shared/adaptiveModalTheme';
import { Platform, Pressable, View, Text } from 'react-native';
import { Dialog, XStack, YStack } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useTranslation } from 'react-i18next';
import { responsiveFontSize } from '@utils/responsiveFontSize';

interface ExceededLimit {
  name: string;
  current: number;
  limit: number;
  percentage: number;
}

interface TrialExpirationModalProps {
  open: boolean;
  onClose: () => void;
  /** If set, "Remind me later" calls this instead of onClose (e.g. to chain decline-feedback flow). */
  onRemindLater?: () => void;
  onSubscribe?: () => void;
  daysRemaining?: number;
  /** Unix ms at trial end — drives live countdown */
  trialEndTimestamp?: number | null;
  benefits?: string[];
  exceededLimits?: ExceededLimit[];
}

function pad2(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

const TrialExpirationModal: React.FC<TrialExpirationModalProps> = ({
  open,
  onClose,
  onRemindLater,
  onSubscribe,
  daysRemaining = 3,
  trialEndTimestamp = null,
  benefits: benefitsProp,
  exceededLimits = []
}) => {
  const theme = themeSelector();
  const hasExceededLimits = exceededLimits.length > 0;
  const { t } = useTranslation();
  const [nowMs, setNowMs] = useState(() => Date.now());

  const benefitItems = useMemo(() => {
    if (benefitsProp && benefitsProp.length > 0) return benefitsProp;
    return [
      t('trialBenefitSavedData'),
      t('trialBenefitPremiumFeatures'),
      t('trialBenefitPrioritySupport'),
      t('trialBenefitUpdates'),
    ];
  }, [benefitsProp, t]);

  useEffect(() => {
    if (!open || !trialEndTimestamp) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, trialEndTimestamp]);

  const countdown = useMemo(() => {
    if (!trialEndTimestamp) {
      return { days: daysRemaining, hours: 0, minutes: 0, seconds: 0, expired: false };
    }
    const totalMs = Math.max(0, trialEndTimestamp - nowMs);
    if (totalMs <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
    }
    const totalSec = Math.floor(totalMs / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    return { days, hours, minutes, seconds, expired: false };
  }, [trialEndTimestamp, nowMs, daysRemaining]);

  const titleFs = responsiveFontSize(20);
  const bodyFs = responsiveFontSize(14);
  const smallFs = responsiveFontSize(12);
  const countdownFs = responsiveFontSize(18);
  const unitFs = responsiveFontSize(11);

  return (
    <Dialog key="trialExpiration" modal open={open}>
      <Dialog.Portal zIndex={MODAL_PORTAL_Z_INDEX}>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: MODAL_OVERLAY_Z_INDEX,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              zIndex: MODAL_CONTENT_Z_INDEX,
              padding: 0,
              minWidth: Platform.OS === 'web' ? 500 : undefined,
              maxWidth: 600,
              width: Platform.OS === 'web' ? undefined : '92%',
              maxHeight: '85%',
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? '#fef3c7' : '#451a03',
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="schedule"
                  size={20}
                  color={theme ? '#f59e0b' : '#fbbf24'}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: titleFs,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  {t('trialEndsSoonTitle')}
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: bodyFs,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  {t('trialDaysRemainingLine', { count: daysRemaining })}
                </Dialog.Description>
              </YStack>
            </XStack>
          </View>

          <View style={{ padding: 24 }}>
            <YStack gap="$4">
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <YStack gap="$3">
                  <Text style={{
                    fontSize: bodyFs,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 8,
                  }}>
                    {t("Don't lose access to your work")}
                  </Text>
                  <Text style={{
                    fontSize: smallFs,
                    color: theme ? '#64748b' : '#94a3b8',
                    lineHeight: 22,
                  }}>
                    {t("Your trial period is ending soon. Subscribe now to continue using all features and keep your data safe. We'll automatically save your progress.")}
                  </Text>
                  {hasExceededLimits && (
                    <View style={{
                      marginTop: 12,
                      padding: 12,
                      backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: theme ? '#fecaca' : '#991b1b',
                    }}>
                      <XStack alignItems="center" gap="$2" marginBottom={8}>
                        <MaterialIcons
                          name="warning"
                          size={18}
                          color={theme ? '#dc2626' : '#fca5a5'}
                        />
                        <Text style={{
                          fontSize: smallFs,
                          fontWeight: '600',
                          color: theme ? '#dc2626' : '#fca5a5',
                        }}>
                          {t('Data Limits Exceeded')}
                        </Text>
                      </XStack>
                      <Text style={{
                        fontSize: smallFs,
                        color: theme ? '#991b1b' : '#fecaca',
                        lineHeight: 18,
                        marginBottom: 8,
                      }}>
                        {t('The following limits have been exceeded:')}
                      </Text>
                      <YStack gap="$1">
                        {exceededLimits.map((limit, index) => (
                          <XStack key={index} alignItems="center" gap="$2">
                            <MaterialIcons
                              name="error"
                              size={16}
                              color={theme ? '#dc2626' : '#fca5a5'}
                            />
                            <Text style={{
                              fontSize: smallFs,
                              color: theme ? '#991b1b' : '#fecaca',
                            }}>
                              {limit.name}: {limit.current} / {limit.limit} ({limit.percentage}%)
                            </Text>
                          </XStack>
                        ))}
                      </YStack>
                    </View>
                  )}
                  <View style={{
                    marginTop: 12,
                    padding: 12,
                    backgroundColor: theme ? '#eff6ff' : '#1e3a5f',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: theme ? '#bfdbfe' : '#3b82c1',
                  }}>
                    <XStack alignItems="center" gap="$2" marginBottom={4}>
                      <MaterialIcons
                        name="info"
                        size={18}
                        color={theme ? '#2563eb' : '#93c5fd'}
                      />
                      <Text style={{
                        fontSize: smallFs,
                        fontWeight: '600',
                        color: theme ? '#1e40af' : '#bfdbfe',
                      }}>
                        {t('After Trial Expires')}
                      </Text>
                    </XStack>
                    <YStack gap="$1" marginTop={4}>
                      <XStack alignItems="flex-start" gap="$2">
                        <MaterialIcons
                          name="block"
                          size={14}
                          color={theme ? '#2563eb' : '#93c5fd'}
                          style={{ marginTop: 2 }}
                        />
                        <Text style={{
                          fontSize: smallFs,
                          color: theme ? '#1e40af' : '#bfdbfe',
                          lineHeight: 18,
                          flex: 1,
                        }}>
                          {t("You won't be able to add new data or share existing data")}
                        </Text>
                      </XStack>
                      <XStack alignItems="flex-start" gap="$2">
                        <MaterialIcons
                          name="lock"
                          size={14}
                          color={theme ? '#2563eb' : '#93c5fd'}
                          style={{ marginTop: 2 }}
                        />
                        <Text style={{
                          fontSize: smallFs,
                          color: theme ? '#1e40af' : '#bfdbfe',
                          lineHeight: 18,
                          flex: 1,
                        }}>
                          {t('Access to premium features will be restricted')}
                        </Text>
                      </XStack>
                    </YStack>
                  </View>
                </YStack>
              </View>

              <View style={{
                backgroundColor: theme ? '#fef3c7' : '#451a03',
                borderRadius: 12,
                padding: 16,
              }}>
                <Text style={{
                  fontSize: smallFs,
                  fontWeight: '600',
                  color: theme ? '#b45309' : '#fcd34d',
                  textAlign: 'center',
                  marginBottom: 10,
                }}>
                  {t('trialTimeRemainingLabel')}
                </Text>
                <XStack alignItems="stretch" justifyContent="center" gap="$2" flexWrap="wrap">
                  {[
                    { value: countdown.days, unit: t('trialCountdownDaysLabel'), singular: t('trialCountdownDayLabel') },
                    { value: countdown.hours, unit: t('trialCountdownHoursLabel'), singular: t('trialCountdownHourLabel') },
                    { value: countdown.minutes, unit: t('trialCountdownMinutesLabel'), singular: t('trialCountdownMinuteLabel') },
                    { value: countdown.seconds, unit: t('trialCountdownSecondsLabel'), singular: t('trialCountdownSecondLabel') },
                  ].map((segment, idx) => (
                    <YStack
                      key={idx}
                      alignItems="center"
                      minWidth={56}
                      padding={8}
                      borderRadius={10}
                      backgroundColor={theme ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.2)'}
                    >
                      <Text style={{
                        fontSize: countdownFs,
                        fontWeight: '700',
                        color: theme ? '#d97706' : '#fbbf24',
                        ...(Platform.OS === 'web' ? { fontVariant: 'tabular-nums' as const } : {}),
                      }}>
                        {idx === 0 ? String(segment.value) : pad2(segment.value)}
                      </Text>
                      <Text style={{
                        fontSize: unitFs,
                        color: theme ? '#92400e' : '#fde68a',
                        marginTop: 2,
                      }}>
                        {segment.value === 1 ? segment.singular : segment.unit}
                      </Text>
                    </YStack>
                  ))}
                </XStack>
                {countdown.expired && (
                  <Text style={{ fontSize: smallFs, color: theme ? '#b45309' : '#fcd34d', textAlign: 'center', marginTop: 8 }}>
                    {t('trialCountdownExpiredHint')}
                  </Text>
                )}
              </View>

              <YStack gap="$2">
                <Text style={{
                  fontSize: bodyFs,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 8,
                }}>
                  {t("What you'll keep:")}
                </Text>
                {benefitItems.map((benefit, index) => (
                  <XStack key={index} alignItems="center" gap="$2">
                    <MaterialIcons
                      name="check-circle"
                      size={20}
                      color={theme ? '#059669' : '#10b981'}
                    />
                    <Text style={{
                      fontSize: bodyFs,
                      color: theme ? '#1e293b' : '#f1f5f9',
                    }}>
                      {benefit}
                    </Text>
                  </XStack>
                ))}
              </YStack>
            </YStack>
          </View>

          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end" flexWrap="wrap">
              <Dialog.Close asChild>
                <Pressable
                  onPress={() => (onRemindLater ?? onClose)()}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: bodyFs,
                  }}>
                    {t('Remind Me Later')}
                  </Text>
                </Pressable>
              </Dialog.Close>
              <Dialog.Close asChild>
                <Pressable
                  onPress={() => {
                    onSubscribe?.();
                    onClose();
                  }}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#3b82f6' : '#2563eb',
                    minWidth: 100,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: '#3b82f6',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: bodyFs,
                  }}>
                    {t('trialSubscribeChoosePlanCta')}
                  </Text>
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default TrialExpirationModal;
